// ============================================================================
// RATE-SHEET FINGERPRINT
// ----------------------------------------------------------------------------
// Deterministic 12-char DJB2-derived hash over the *trusted* portion of the
// business pricing rate sheet. Stamped onto every cost recompute and embedded
// into `_costingPricingFingerprint` so a stored quote can be compared against
// the current rate sheet to detect "this quote was costed against an older
// rate sheet" drift.
//
// Closes audit findings F-05 (`_costingPricingFingerprint` ignores rate-sheet
// rows, so a rate edit doesn't invalidate stored sheets) and F-06
// (`recomputeCostBreakdown` return shape carries no rate-sheet identity, so
// you can't tell which rate sheet an item was costed against).
//
// What goes into the hash
// -----------------------
//   • Trusted catalogue rows in the four rate categories:
//     `material_costs`, `labour_rates`, `equipment`, `subcontractors`.
//     Each row contributes its id, name, aliases, unit, cost/sell semantics and,
//     for subcontractors, the reusable supplier identity.
//   • The admin fee `administration.quote_administration_fee` (single scalar
//     that affects every quote).
//   • The full `costingConfig` (overhead / contingency / margin brackets).
//
// What is excluded (HARD)
// -----------------------
//   • Lifecycle metadata: `usage_count`, `last_used_at`, `updated_at`,
//     `created_at`. `batchIncrementUsage` mutates these on every quote run;
//     including them would invalidate every quote on every harvest.
//   • Rows in `pending_review` state (i.e. rows that fail
//     `isTrustedPricingItem`). They are not yet usable in pricing; the moment
//     the operator approves them, they cross into the trusted set and the
//     fingerprint changes — by design.
//   • `business_info` and `founderStrategy` (irrelevant or already covered by
//     `projectDefaultMargin`).
//
// Hash function
// -------------
// Two interleaved DJB2 streams concatenated to a 12-char hex string. 48 bits
// is plenty for change-detection across one tenant's rate-sheet history; this
// is NOT a security boundary, it's a "did anything that matters change?"
// signal. Cheap, deterministic, no crypto dependency.
// ============================================================================

import { BusinessPricingService } from './pricing.js';
import { hashCanonicalJson } from '../quote-engine/canonical-json';
import { isPerDayPricingUnit } from '../utils/pricing-unit-semantics.js';

const TRACKED_CATEGORIES = ['material_costs', 'labour_rates', 'equipment', 'subcontractors'];

function _isTrusted(item) {
    // Mirrors `isTrustedPricingItem` from pricing.js (not exported but the
    // service exposes it on the singleton). Falling back to an inline check
    // keeps the module self-contained against future export shape changes.
    if (!item) return false;
    if (typeof BusinessPricingService.isTrustedPricingItem === 'function') {
        return BusinessPricingService.isTrustedPricingItem(item);
    }
    if (item.review_status === 'pending_review') return false;
    if (item.source === 'ai_discovered' && item.review_status !== 'approved') return false;
    return true;
}

function _rowName(item, catKey) {
    // labour_rates + subcontractors store the display label as `trade`;
    // material_costs + equipment store it as `item`. Either may also have a
    // `name` (subcontractors sometimes have both `name` and `trade`).
    if (catKey === 'labour_rates' || catKey === 'subcontractors') {
        return String(item.trade || item.name || item.item || '');
    }
    return String(item.item || item.name || item.trade || '');
}

function _rowRate(item, catKey) {
    // labour_rates + subcontractors use `rate`; material_costs + equipment
    // use `cost`. Older rows may carry `unitRate` from prior schemas.
    if (catKey === 'labour_rates' || catKey === 'subcontractors') {
        const v = Number(item.rate ?? item.unitRate ?? item.cost ?? 0);
        return Number.isFinite(v) ? _round(v) : 0;
    }
    const v = Number(item.cost ?? item.unitRate ?? item.rate ?? 0);
    return Number.isFinite(v) ? _round(v) : 0;
}

function _rowAliases(item) {
    return Array.isArray(item?.aliases)
        ? item.aliases
            .map(alias => String(alias || '').replace(/\s+/g, ' ').trim())
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b))
        : [];
}

function _rowPricingSemantics(item) {
    const costRate = Number(item?.costRate ?? item?.cost_rate);
    return {
        costRate: Number.isFinite(costRate) ? _round(costRate) : null,
        costRateTaxBasis: String(item?.cost_rate_tax_basis ?? item?.costRateTaxBasis ?? ''),
        chargeOutTaxBasis: String(item?.charge_out_tax_basis ?? item?.chargeOutTaxBasis ?? ''),
        finalChargeOut: item?.rate_is_final_charge_out === true || item?.rateIsFinalChargeOut === true,
        isDayRate: isPerDayPricingUnit(
            item?.unit,
            item?.is_day_rate === true || item?.isDayRate === true,
        ),
        selectableForScope: item?.selectable_for_scope !== false,
    };
}

function _rowSupplierSemantics(item, catKey) {
    if (catKey !== 'subcontractors') return {};
    return {
        supplierId: String(item?.supplierId || ''),
        contractorName: String(item?.contractorName || ''),
        supplierType: String(item?.supplierType || 'subcontractor'),
    };
}

function _round(n, digits = 4) {
    if (!Number.isFinite(n)) return null;
    const f = 10 ** digits;
    return Math.round(n * f) / f;
}

function _stableCanon(value) {
    if (Array.isArray(value)) return value.map(_stableCanon);
    if (value && typeof value === 'object') {
        return Object.keys(value)
            .sort()
            .reduce((out, key) => {
                const v = value[key];
                if (typeof v !== 'function' && v !== undefined) out[key] = _stableCanon(v);
                return out;
            }, {});
    }
    if (typeof value === 'number') return Number.isFinite(value) ? _round(value) : null;
    return value ?? null;
}

function _hashString12(value) {
    // Two interleaved DJB2 streams concatenated; 12-char hex. Deterministic,
    // no crypto dep. 8 chars from stream A + 4 chars from stream B = 12.
    let h1 = 5381;
    let h2 = 52711;
    const s = String(value);
    for (let i = 0; i < s.length; i++) {
        const c = s.charCodeAt(i);
        h1 = (((h1 << 5) + h1) + c) | 0;          // h1 * 33 + c
        h2 = (((h2 << 5) + h2) ^ c) | 0;          // h2 * 33 ^ c
    }
    const a = (h1 >>> 0).toString(16).padStart(8, '0');
    const b = (h2 >>> 0).toString(16).padStart(8, '0').slice(0, 4);
    return a + b;
}

/**
 * Deterministic 12-char fingerprint of the rate-sheet inputs that affect
 * pricing. Returns `null` for null/undefined input so callers can choose
 * their own legacy-quote messaging ("rate sheet unknown — costed before
 * v5.19.36"). Returns a stable hash for an empty rate sheet (`{}`) so
 * "fresh blank tenant" still produces a comparable identifier.
 *
 * @param {Object|null} businessPricing The shape returned by
 *     `BusinessPricingService.getData()`.
 * @returns {string|null} 12-char hex string, or null when input is missing.
 */
export function canonicalRatesheetAuthority(businessPricing) {
    if (businessPricing == null) return null;
    const data = (typeof businessPricing === 'object') ? businessPricing : {};

    const rows = [];
    for (const catKey of TRACKED_CATEGORIES) {
        const items = Array.isArray(data[catKey]) ? data[catKey] : [];
        for (const it of items) {
            if (!_isTrusted(it)) continue;
            rows.push({
                cat: catKey,
                id: String(it.id || ''),
                name: _rowName(it, catKey),
                aliases: _rowAliases(it),
                unit: String(it.unit || ''),
                rate: _rowRate(it, catKey),
                ..._rowPricingSemantics(it),
                ..._rowSupplierSemantics(it, catKey),
            });
        }
    }
    // Order-independent: sort rows by cat+id (id is stable; ties on missing
    // id fall through to lexical order on the empty-string suffix, which is
    // still deterministic for a given input).
    rows.sort((a, b) => {
        const aKey = a.cat + '\0' + a.id + '\0' + a.name;
        const bKey = b.cat + '\0' + b.id + '\0' + b.name;
        return aKey < bKey ? -1 : aKey > bKey ? 1 : 0;
    });

    const adminFee = Number(data?.administration?.quote_administration_fee);
    const costingConfig = data?.costingConfig || null;

    return _stableCanon({
        rows,
        admin: {
            quoteAdminFee: Number.isFinite(adminFee) ? _round(adminFee, 2) : 0,
        },
        costingConfig,
        rateCardAuthority: data?.rateCardAuthority || data?.rate_card_authority || null,
    });
}

/**
 * Cryptographic semantic identity for the legacy trusted-rate projection.
 * Evaluation provenance additionally binds the complete detached pricing
 * payload because matching order, review state and usage tie-breaks can affect
 * execution even when this trusted projection remains unchanged.
 */
export function ratesheetTrustedProjectionHash(businessPricing) {
    const authority = canonicalRatesheetAuthority(businessPricing);
    return authority == null ? null : hashCanonicalJson(authority);
}

export function ratesheetFingerprint(businessPricing) {
    const canon = canonicalRatesheetAuthority(businessPricing);
    if (canon == null) return null;

    return _hashString12(JSON.stringify(canon));
}

/**
 * Internal helper exposed for unit tests. Lets the test suite assert the
 * canon shape without re-implementing the row-trim logic.
 */
export function _ratesheetCanonForTests(businessPricing) {
    if (businessPricing == null) return null;
    const data = (typeof businessPricing === 'object') ? businessPricing : {};
    const rows = [];
    for (const catKey of TRACKED_CATEGORIES) {
        const items = Array.isArray(data[catKey]) ? data[catKey] : [];
        for (const it of items) {
            if (!_isTrusted(it)) continue;
            rows.push({
                cat: catKey,
                id: String(it.id || ''),
                name: _rowName(it, catKey),
                aliases: _rowAliases(it),
                unit: String(it.unit || ''),
                rate: _rowRate(it, catKey),
                ..._rowPricingSemantics(it),
                ..._rowSupplierSemantics(it, catKey),
            });
        }
    }
    rows.sort((a, b) => {
        const aKey = a.cat + '\0' + a.id + '\0' + a.name;
        const bKey = b.cat + '\0' + b.id + '\0' + b.name;
        return aKey < bKey ? -1 : aKey > bKey ? 1 : 0;
    });
    return _stableCanon({
        rows,
        admin: {
            quoteAdminFee: Number.isFinite(Number(data?.administration?.quote_administration_fee))
                ? _round(Number(data.administration.quote_administration_fee), 2)
                : 0,
        },
        costingConfig: data?.costingConfig || null,
        rateCardAuthority: data?.rateCardAuthority || data?.rate_card_authority || null,
    });
}
